import {
  parseISO,
  eachDayOfInterval,
  format,
  startOfMonth,
  endOfMonth,
  getDay,
  isSameDay,
  isWithinInterval,
  differenceInDays,
} from "date-fns";
import { useState, useEffect } from "react";
import { calculateHolidayHours } from "./holidayHours";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  BellAlertIcon,
  CalendarDaysIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  SunIcon,
} from "@heroicons/react/20/solid";

export default function HolidayChart({ onTriggerParentEffect, holiday }) {
  const [holidayData, setHolidayData] = useState([]);
  const [daysOpen, setDaysOpen] = useState("");
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [selectedRange, setSelectedRange] = useState({
    FromDate: null,
    ToDate: null,
  });
  const [holidayLength, setHolidayLength] = useState("Full");
  const [reason, setReason] = useState("");
  const [amPm, setAmPm] = useState("");
  const [userDetails, setUserDetails] = useState([]);
  const [policy, setPolicy] = useState([]);
  const [Emergency, setEmergency] = useState("No");
  const [successMessage, setSuccessMessage] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [maxOnLeave, setMaxOnLeave] = useState(0);
  const [departmentHolidays, setDepartmentHolidays] = useState([]);
  const [bankHolidays, setBankHolidays] = useState({});
  const [showBankHolidays, setShowBankHolidays] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  console.log(holiday);

  useEffect(() => {
    const fetchStaff = async () => {
      const response = await fetch("https://api.insightos.co.uk/api/staff", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const staffData = await response.json();
      console.log("Fetched staff data:", staffData);
      setStaffList(staffData.data);
    };

    fetchStaff();
  }, []);

  useEffect(() => {
    if (!selectedStaff) return;

    console.log("Selected staff changed:", selectedStaff);

    const fetchData = async () => {
      const response = await fetch("https://api.insightos.co.uk/api/holiday", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const data = await response.json();
      console.log("Fetched holiday data:", data);
      const filteredData = data.data.filter(
        (holiday) => holiday.Approved === 1 && holiday.Department === selectedStaff.Department
      );
      console.log("Filtered holiday data by department:", selectedStaff.Department, filteredData);
      setDepartmentHolidays(filteredData);

      setBankHolidays(data.bankHolidays);

      const departments = data.departments;

      const userDepartment = departments.find(
        (dept) => dept.Name === selectedStaff.Department     
      );

      if (userDepartment) {
        setMaxOnLeave(userDepartment.MaxOnLeave);
        console.log("Set maxOnLeave to:", userDepartment.MaxOnLeave);
      }

      const response2 = await fetch("https://api.insightos.co.uk/api/policies", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const policyData = await response2.json();
      console.log("Fetched policy data:", policyData);
      setPolicy(policyData.data);

      const daysOpen = policyData.data[0].DaysOpen;
      setDaysOpen(daysOpen);

      setUserDetails([selectedStaff]);
      console.log("Set userDetails to selectedStaff:", selectedStaff);

      const holidaysByMonth = calculateHolidaysByMonth(filteredData, daysOpen);
      console.log("Calculated holidays by month:", holidaysByMonth);
      setHolidayData(holidaysByMonth);
    };

    fetchData();
    // eslint-disable-next-line
  }, [selectedStaff]);

  const calculateHolidaysByMonth = (holidayRequests, daysOpen) => {
    const holidaysByMonth = Array.from({ length: 12 }, (_, i) => ({
      month: i,
      taken: 0,
      totalAvailableDays: 0,
      percentageTaken: 0,
      availableDays: [],
      takenDays: [],
      year: 0,
      monthName: "",
    }));

    holidayRequests.forEach((holiday) => {
      const fromDate = parseISO(holiday.FromDate);
      const toDate = parseISO(holiday.ToDate);
      const month = fromDate.getMonth();

      const daysBetween = eachDayOfInterval({ start: fromDate, end: toDate });
      const dayNames = daysBetween.map((day) => format(day, "EEEE"));
      const matchedDays = daysBetween.filter((day, idx) =>
        daysOpen.includes(dayNames[idx])
      );

      holidaysByMonth[month].taken += matchedDays.length;
      holidaysByMonth[month].takenDays.push(
        ...matchedDays.map((d) => d.getDate())
      );
    });

    const currentYear = new Date().getFullYear();
    holidaysByMonth.forEach((monthData, index) => {
      const startOfMonthDate = new Date(currentYear, index, 1);
      const endOfMonthDate = new Date(currentYear, index + 1, 0);

      const daysInMonth = eachDayOfInterval({
        start: startOfMonthDate,
        end: endOfMonthDate,
      });

      const workingDaysInMonth = daysInMonth.filter((day) => {
        const dayName = format(day, "EEEE");
        return daysOpen.includes(dayName);
      });

      monthData.monthName = format(startOfMonthDate, "MMMM");
      monthData.totalAvailableDays = workingDaysInMonth.length;
      monthData.availableDays = workingDaysInMonth.map((d) => d.getDate());
      monthData.year = currentYear;

      if (monthData.totalAvailableDays > 0) {
        monthData.percentageTaken =
          (monthData.taken / monthData.totalAvailableDays) * 100;
      }
    });

    return holidaysByMonth;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("Submitting holiday request with:", { userHasHoliday, isOverEntitlement, isOverMaxOnLeave, Emergency });

    if ((userHasHoliday || isOverEntitlement || isOverMaxOnLeave) && Emergency !== "Yes") {
      console.log("Submission blocked due to conflict or over entitlement");
      return;
    }

    const formattedFromDate = format(selectedRange.FromDate, "yyyy-MM-dd");
    const formattedToDate = format(selectedRange.ToDate, "yyyy-MM-dd");

    const submissionData = {
      FromDate: formattedFromDate,
      ToDate: formattedToDate,
      Name: userDetails[0]?.Name,
      Policy: policy[0]?.HolidayType,
      Notes: reason,
      Approved: 0,
      Role: userDetails[0]?.Role,
      StaffId: userDetails[0]?.id,
      Emergency,
      ContractedHours: userDetails[0]?.ContractType === 0 ? userDetails[0]?.AverageHours : userDetails[0]?.ContractedHours,
      ContractedDays: userDetails[0]?.ContractType === 0 ? userDetails[0]?.AverageDays : userDetails[0]?.ContractedDays,
      Hours: totalHolidayHours,
      FullDay: holidayLength,
      Start: amPm,
      Manager: selectedStaff?.Manager
    };

    console.log("Submission data:", submissionData);

    const response = await fetch(
      "https://api.insightos.co.uk/api/holidayrequests",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(submissionData),
      }
    );

    console.log("Submission response:", response);

    if (!response.ok) {
      console.log("Submission failed");
      return;
    }

    console.log("Submission successful");
    onTriggerParentEffect();
    setSubmitted(true);
  };

  const handleDayClick = (day) => {
    if (!selectedRange.FromDate) {
      setSelectedRange({ FromDate: day, ToDate: null });
    } else if (
      selectedRange.FromDate &&
      !selectedRange.ToDate &&
      day > selectedRange.FromDate
    ) {
      setSelectedRange({ FromDate: selectedRange.FromDate, ToDate: day });
    } else {
      setSelectedRange({ FromDate: day, ToDate: null });
    }
  };

  // --- HOLIDAY CALCULATION LOGIC (with break deduction) ---
  const calculateHolidayDetails = () => {
    if (!userDetails.length || !policy.length) {
      return {
        totalHolidayHours: 0,
        remainingTime: 0,
        userHasHoliday: false,
        isOverEntitlement: false,
        isOverMaxOnLeave: false,
        overlappingCount: 0,
        requested: 0,
      };
    }

    const currentUser = userDetails[0];
    const currentPolicy = policy[0];

    let totalHolidayHours = 0;
    let remainingTime = 0;
    let isOverEntitlement = false;
    let requested = 0;

    const from = selectedRange.FromDate
      ? parseISO(format(selectedRange.FromDate, "yyyy-MM-dd"))
      : null;
    const to = selectedRange.ToDate
      ? parseISO(format(selectedRange.ToDate, "yyyy-MM-dd"))
      : null;

    if (!from || !to) {
      return {
        totalHolidayHours: 0,
        remainingTime: 0,
        userHasHoliday: false,
        isOverEntitlement: false,
        isOverMaxOnLeave: false,
        overlappingCount: 0,
        requested: 0,
      };
    }

    const daysBetween = differenceInDays(to, from) + 1;
    let workingDays = 0;
    for (let i = 0; i < daysBetween; i++) {
      const currentDate = new Date(from);
      currentDate.setDate(currentDate.getDate() + i);
      const dayName = format(currentDate, "EEEE");
      if (daysOpen.includes(dayName)) {
        workingDays++;
      }
    }

    // Overlapping holidays in department
    const overlappingHolidays = departmentHolidays.filter((h) => {
      const hFrom = parseISO(h.FromDate);
      const hTo = parseISO(h.ToDate);
      return (from >= hFrom && from <= hTo) || (to >= hFrom && to <= hTo) || (hFrom >= from && hFrom <= to) || (hTo >= from && hTo <= to);
    });
    const overlappingCount = overlappingHolidays.length;
    const isOverMaxOnLeave = overlappingCount >= maxOnLeave;

    // Check if current user already has holiday in this period
    const userHasHoliday = holiday.some((h) => {
      const hFrom = parseISO(h.FromDate);
      const hTo = parseISO(h.ToDate);
      return (from >= hFrom && from <= hTo) || (to >= hFrom && to <= hTo);
    });

    if (currentPolicy.HolidayType === "Hours") {
      totalHolidayHours = calculateHolidayHours(currentUser, holidayLength, workingDays);
      requested = totalHolidayHours;
      remainingTime = currentUser.HolidayEntitlement - currentUser.HolidayTaken - totalHolidayHours;
    } else if (currentPolicy.HolidayType === "Days") {
      if (holidayLength === "Half") {
        totalHolidayHours = 0.5;
        requested = totalHolidayHours;
      } else if (holidayLength === "Full") {
        totalHolidayHours = 1;
        requested = totalHolidayHours;
      } else if (holidayLength === "Block") {
        totalHolidayHours = workingDays;
        requested = totalHolidayHours;
      }
      remainingTime = currentUser.HolidayEntitlement - currentUser.HolidayTaken - totalHolidayHours;
    }

    if (remainingTime < 0) {
      isOverEntitlement = true;
      remainingTime = 0;
      if (Emergency !== "Yes") totalHolidayHours = 0;
    }

    return {
      totalHolidayHours,
      remainingTime,
      userHasHoliday,
      isOverEntitlement,
      isOverMaxOnLeave,
      overlappingCount,
      requested,
    };
  };

  const {
    totalHolidayHours,
    remainingTime,
    userHasHoliday,
    isOverEntitlement,
    isOverMaxOnLeave,
    overlappingCount,
    requested,
  } = calculateHolidayDetails();

  const noAvailabilityDay = (() => {
    if (holidayLength !== "Full" || !selectedRange.FromDate || !daysOpen) return false;
    const dayName = format(selectedRange.FromDate, "EEEE");
    if (!daysOpen.includes(dayName)) return true;
    if (isOverMaxOnLeave) return true;
    return false;
  })();

  const renderCalendar = (monthData) => {
    const firstDayOfMonth = startOfMonth(new Date(currentYear, monthData.month, 1));
    const lastDayOfMonth = endOfMonth(firstDayOfMonth);
    const daysInMonth = eachDayOfInterval({ start: firstDayOfMonth, end: lastDayOfMonth });

    const firstWeekday = (getDay(firstDayOfMonth) + 6) % 7;
    const emptyDaysBefore = Array(firstWeekday).fill(null);

    const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => setCurrentMonth((prev) => (prev - 1 + 12) % 12)}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-all duration-200"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          
          <h3 className="text-lg font-bold text-gray-900">
            {format(new Date(currentYear, currentMonth), "MMMM yyyy")}
          </h3>
          
          <button
            type="button"
            onClick={() => setCurrentMonth((prev) => (prev + 1) % 12)}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-all duration-200"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Weekday Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekdays.map((day, idx) => (
            <div key={idx} className="text-center text-xs font-semibold text-gray-600 py-2">
              <span className="hidden sm:block">{day}</span>
              <span className="sm:hidden">{day.charAt(0)}</span>
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {[...emptyDaysBefore, ...daysInMonth].map((day, dayIdx) => {
            const dayNum = day ? day.getDate() : null;
            const isAvailable = monthData.availableDays.includes(dayNum);
            const isTaken = monthData.takenDays.includes(dayNum);

            const isSelected =
              (selectedRange.FromDate && isSameDay(day, selectedRange.FromDate)) ||
              (selectedRange.ToDate && isSameDay(day, selectedRange.ToDate));
            const isInRange =
              selectedRange.FromDate &&
              selectedRange.ToDate &&
              isWithinInterval(day, { start: selectedRange.FromDate, end: selectedRange.ToDate });

            let buttonClasses = "relative w-full h-10 sm:h-12 flex items-center justify-center text-sm font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500";
            
            if (!day) {
              buttonClasses += " invisible";
            } else if (isSelected) {
              buttonClasses += " bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg";
            } else if (isInRange) {
              buttonClasses += " bg-blue-100 text-blue-900 border-2 border-blue-300";
            } else if (isAvailable) {
              if (isTaken) {
                buttonClasses += " bg-gradient-to-r from-red-500 to-red-600 text-white cursor-not-allowed";
              } else {
                buttonClasses += " bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 cursor-pointer";
              }
            } else {
              buttonClasses += " bg-gray-200 text-gray-400 cursor-not-allowed";
            }

            return (
              <button
                key={dayNum || `empty-${dayIdx}`}
                type="button"
                onClick={() => day && isAvailable && !isTaken && handleDayClick(day)}
                className={buttonClasses}
                disabled={!day || !isAvailable || isTaken}
              >
                {dayNum && (
                  <>
                    <span className="relative z-10">{dayNum}</span>
                    {isTaken && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-1 h-1 bg-white rounded-full opacity-75"></div>
                      </div>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-gradient-to-r from-green-500 to-green-600 rounded"></div>
            <span className="text-gray-600">Available</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-gradient-to-r from-red-500 to-red-600 rounded"></div>
            <span className="text-gray-600">Taken</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></div>
            <span className="text-gray-600">Selected</span>
          </div>
        </div>
      </div>
    );
  };

  const monthData = holidayData[currentMonth];
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="w-full sm:max-w-7xl sm:mx-auto px-0 sm:px-6 lg:px-8 py-0 sm:py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <SunIcon className="h-7 w-7 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Holiday Request</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Plan your time off with our easy-to-use holiday booking system. Select your dates and submit your request.
          </p>
        </div>

        {/* Staff Selection */}
        <div className="max-w-md mx-auto mb-8">
          <label htmlFor="staff" className="block text-sm font-semibold text-gray-900 mb-2">
            Select Staff Member
          </label>
          <select
            id="staff"
            name="staff"
            value={selectedStaff ? selectedStaff.id : ""}
            onChange={(e) => {
              const staff = staffList.find(s => s.id === parseInt(e.target.value));
              console.log("Staff selected from dropdown:", staff);
              setSelectedStaff(staff);
            }}
            className="block w-full rounded-xl border border-gray-300 py-3 px-4 text-gray-900 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
          >
            <option value="">Select a staff member</option>
            {staffList.map((staff) => (
              <option key={staff.id} value={staff.id}>
                {staff.Name} - {staff.Department}
              </option>
            ))}
          </select>
        </div>

        {submitted ? (
          <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
            <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mb-6 shadow-lg">
              <CheckCircleIcon className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Sent</h2>
            <p className="text-gray-600 text-lg mb-2">Please Close</p>
            <p className="text-sm text-gray-400">The holiday request has been submitted for approval.</p>
          </div>
        ) : selectedStaff && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* Form Section */}
          <div className="order-2 xl:order-1">
            <form className="bg-white rounded-none sm:rounded-2xl shadow-xl border-0 sm:border border-gray-200 overflow-hidden" onSubmit={handleSubmit}>
              {/* Form Header */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
                <div className="flex items-center">
                  <CalendarDaysIcon className="h-6 w-6 text-white mr-3" />
                  <h2 className="text-xl font-bold text-white">Request Details</h2>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Bank Holidays Section */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <button
                    type="button"
                    onClick={() => setShowBankHolidays(!showBankHolidays)}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
                  >
                    {showBankHolidays ? 'Hide' : 'Show'} Bank Holidays
                  </button>
                  {showBankHolidays && (
                    <div className="mt-4">
                      <table className="w-full text-sm text-left">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                          <tr>
                            <th scope="col" className="px-6 py-3">Holiday</th>
                            <th scope="col" className="px-6 py-3">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(bankHolidays['england-and-wales']?.events || []).map((event, index) => (
                            <tr key={index} className="bg-white border-b">
                              <td className="px-6 py-4 font-medium text-gray-900">{event.title}</td>
                              <td className="px-6 py-4">{format(parseISO(event.date), "dd MMMM yyyy")}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Holiday Length */}
                <div>
                  <label htmlFor="fullDay" className="block text-sm font-semibold text-gray-900 mb-2">
                    Holiday Type
                  </label>
                  <select
                    onChange={(e) => setHolidayLength(e.target.value)}
                    id="fullDay"
                    name="fullDay"
                    value={holidayLength}
                    className="block w-full rounded-xl border border-gray-300 py-3 px-4 text-gray-900 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  >
                    <option value="Full">Full Day</option>
                    <option value="Half">Half Day</option>
                    <option value="Block">Block Booking</option>
                  </select>
                </div>

                {/* Half Day Options */}
                {holidayLength === "Half" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="amPm" className="block text-sm font-semibold text-gray-900 mb-2">
                        Time Period
                      </label>
                      <select
                        id="amPm"
                        name="amPm"
                        value={amPm}
                        onChange={(e) => setAmPm(e.target.value)}
                        className="block w-full rounded-xl border border-gray-300 py-3 px-4 text-gray-900 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      >
                        <option value="AM">Morning (AM)</option>
                        <option value="PM">Afternoon (PM)</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="startEnd" className="block text-sm font-semibold text-gray-900 mb-2">
                        Select Date
                      </label>
                      <input
                        onChange={(e) =>
                          setSelectedRange({
                            FromDate: parseISO(e.target.value),
                            ToDate: parseISO(e.target.value),
                          })
                        }
                        type="date"
                        id="startEnd"
                        name="startEnd"
                        className="block w-full rounded-xl border border-gray-300 py-3 px-4 text-gray-900 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      />
                    </div>
                  </div>
                )}

                {/* Full Day Options */}
                {holidayLength === "Full" && (
                  <div>
                    <label htmlFor="startEnd" className="block text-sm font-semibold text-gray-900 mb-2">
                      Select Date
                    </label>
                    {noAvailabilityDay && (
                      <div className="mb-2 flex items-center gap-2 bg-red-50 border border-red-300 rounded-xl px-4 py-3">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0" />
                        <p className="text-sm font-semibold text-red-700">No Availability On This Day</p>
                      </div>
                    )}
                    <input
                      onChange={(e) =>
                        setSelectedRange({
                          FromDate: parseISO(e.target.value),
                          ToDate: parseISO(e.target.value),
                        })
                      }
                      type="date"
                      id="startEnd"
                      name="startEnd"
                      className={`block w-full rounded-xl border py-3 px-4 text-gray-900 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${noAvailabilityDay ? "border-red-400 bg-red-50" : "border-gray-300"}`}
                    />
                  </div>
                )}

                {/* Selected Dates Display */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Selected Dates</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
                      <p className="text-sm font-semibold text-gray-900 bg-white rounded-lg px-3 py-2 border">
                        {selectedRange.FromDate ? format(selectedRange.FromDate, "dd MMMM yyyy") : "Select a date"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
                      <p className="text-sm font-semibold text-gray-900 bg-white rounded-lg px-3 py-2 border">
                        {selectedRange.ToDate ? format(selectedRange.ToDate, "dd MMMM yyyy") : "Select a date"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <label htmlFor="reason" className="block text-sm font-semibold text-gray-900 mb-2">
                    Reason for Holiday <span className="text-gray-500 font-normal">(Optional)</span>
                  </label>
                  <textarea
                    onChange={(e) => setReason(e.target.value)}
                    id="reason"
                    name="reason"
                    rows={3}
                    placeholder="Enter the reason for your holiday request..."
                    className="block w-full rounded-xl border border-gray-300 py-3 px-4 text-gray-900 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none"
                  />
                </div>

                {/* Emergency Holiday */}
                <div>
                  <label htmlFor="Emergency" className="block text-sm font-semibold text-gray-900 mb-2">
                    Emergency Holiday
                  </label>
                  <select
                    id="Emergency"
                    name="Emergency"
                    value={Emergency}
                    onChange={(e) => setEmergency(e.target.value)}
                    className="block w-full rounded-xl border border-gray-300 py-3 px-4 text-gray-900 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  data-component="holiday-request"
                  data-time-saved="10"
                  data-feature="submit_team_member_holiday_post"
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02]"
                >
                  <span className="flex items-center justify-center">
                    <SunIcon className="h-5 w-5 mr-2" />
                    Submit Holiday Request
                  </span>
                </button>
              </div>
            </form>
          </div>

          {/* Calendar & Details Section */}
          <div className="order-1 xl:order-2 space-y-6">
            {/* Calendar for Block Booking */}
            {holidayLength === "Block" && (
              <div>
                {monthData && renderCalendar(monthData)}
              </div>
            )}

            {/* Holiday Details Card */}
            <div className="bg-white rounded-none sm:rounded-2xl shadow-xl border-0 sm:border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 to-blue-600 px-6 py-4">
                <div className="flex items-center">
                  <ClockIcon className="h-6 w-6 text-white mr-3" />
                  <h3 className="text-xl font-bold text-white">Holiday Summary</h3>
                </div>
              </div>
              
              <div className="p-6">
                {/* Warning Messages */}
                {(userHasHoliday || isOverEntitlement || isOverMaxOnLeave) && (
                  <div className={`mb-6 p-4 rounded-xl border-2 ${
                    userHasHoliday || isOverEntitlement || isOverMaxOnLeave
                      ? "bg-red-50 border-red-300"
                      : "border-gray-200"
                  }`}>
                    <div className="flex items-start">
                      <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        {userHasHoliday && (
                          <p className="text-sm font-semibold text-red-800 mb-1">
                            Holiday Conflict Detected
                          </p>
                        )}
                        {isOverMaxOnLeave && (
                          <p className="text-sm font-semibold text-red-800 mb-1">
                            Department Leave Limit Exceeded
                          </p>
                        )}
                        {isOverEntitlement && (
                          <p className="text-sm font-semibold text-red-800 mb-1">
                            Over Entitlement Limit
                          </p>
                        )}
                        <div className="text-sm text-red-700">
                          {userHasHoliday && <p className="mb-1">This staff member already has a holiday booked in this period.</p>}
                          {isOverMaxOnLeave && <p className="mb-1">Maximum {maxOnLeave} staff can be on leave at the same time. Currently {overlappingCount} staff would be on leave.</p>}
                          {isOverEntitlement && <p>This holiday request exceeds the staff member's remaining entitlement.</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Details Grid */}
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-blue-50 rounded-xl p-4">
                      <div className="text-xs font-medium text-blue-600 mb-1">Holiday Type</div>
                      <div className="text-lg font-bold text-blue-900">{holidayLength}</div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4">
                      <div className="text-xs font-medium text-green-600 mb-1">Requested Time</div>
                      <div className="text-lg font-bold text-green-900">
                        {requested ? requested.toFixed(2) : "0.00"} {policy[0]?.HolidayType === "Days" ? "Days" : "Hours"}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="bg-purple-50 rounded-xl p-4">
                      <div className="text-xs font-medium text-purple-600 mb-1">Hours to Deduct</div>
                      <div className="text-lg font-bold text-purple-900">
                        {totalHolidayHours ? totalHolidayHours.toFixed(2) : "0.00"} {policy[0]?.HolidayType === "Days" ? "Days" : "Hours"}
                      </div>
                    </div>
                    <div className="bg-orange-50 rounded-xl p-4">
                      <div className="text-xs font-medium text-orange-600 mb-1">Remaining After</div>
                      <div className="text-lg font-bold text-orange-900">
                        {remainingTime ? remainingTime.toFixed(2) : "0.00"} {policy[0]?.HolidayType === "Days" ? "Days" : "Hours"}
                      </div>
                    </div>
                  </div>

                  {/* Additional Details */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="text-xs font-medium text-gray-600 mb-2">Additional Information</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Reason:</span>
                        <span className="font-medium text-gray-900">
                          {reason || <span className="italic text-gray-400">Not specified</span>}
                        </span>
                      </div>
                      {holidayLength === "Half" && amPm && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Time Period:</span>
                          <span className="font-medium text-gray-900">{amPm}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Emergency:</span>
                        <span className={`font-medium ${Emergency === "Yes" ? "text-red-600" : "text-gray-900"}`}>
                          {Emergency}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}